/**
 * The Tauri commands the Run section uses (Run & Share, Phase 1).
 *
 * Reading and writing launch.json, resolving a cwd, probing a port and
 * opening the preview window are Rust (`run_config.rs`, `run_preview.rs`).
 * STARTING a configuration is deliberately not: it is `terminal_create` with
 * a launch spec and no xterm, so PATH resolution, the Windows `.cmd` refusal
 * and PTY kill behave exactly as they do in a terminal pane.
 *
 * `@tauri-apps/api/core` is imported lazily (tests/tauriImportsAreLazy).
 */

async function invoke(cmd, args) {
  const core = await import('@tauri-apps/api/core');
  return core.invoke(cmd, args);
}

/** → { source, file?, configurations, skipped, error? } */
export const loadRunConfigs = (dir) => invoke('run_config_load', { dir });

/** Writes `<dir>/.apiboo/launch.json`. */
export const saveRunConfigs = (dir, configurations) => invoke('run_config_save', { dir, configurations });

/** The absolute cwd for a configuration's repo-relative `cwd`. */
export const resolveRunCwd = (dir, cwd) => invoke('run_config_resolve_cwd', { dir, cwd: cwd || null });

/** Is something listening on localhost:port (IPv4 or IPv6)? */
export const portReady = (port) => invoke('run_port_ready', { port });

/** Open (or focus) the preview window on http://localhost:<port><path>. */
export const openPreview = (port, path = '/', title = '') => invoke('run_preview_open', { port, path, title });

/**
 * Start a configuration in a PTY with no pane attached. 120×30 is wide
 * enough that dev servers do not wrap their banner. `killTree`: Rust tracks
 * the whole process tree (Job Object on Windows, process group on Unix), so
 * a stop — or the app quitting — leaves no npm → node → esbuild behind
 * (terminal/process_tree.rs).
 */
export const startProcess = (cwd, config) =>
  invoke('terminal_create', {
    cwd,
    cols: 120,
    rows: 30,
    launch: {
      program: config.runtimeExecutable,
      args: config.runtimeArgs || [],
      env: Object.entries(config.env || {}).map(([name, value]) => ({ name, value: String(value) })),
    },
    killTree: true,
  });

export const killProcess = (id) => invoke('terminal_kill', { id });

/** The first port at or above `from` that is free and bindable on loopback (Rust run_port.rs). */
export const freePort = (from) => invoke('run_free_port', { from });

/**
 * `config` changed to listen on `port` for one run: Vite `--port N --strictPort`,
 * Next `-p N`, Astro `--port N`, otherwise `PORT=N` (CRA). → { configuration, how }
 */
export const portOverride = (dir, config, port) => invoke('run_port_override', { dir, configuration: config, port });
